import type { CategoryName } from '@client/constants/categories';
import type { Question } from '@client/models/cancellation-questions';
import type { LocalDate } from '@client/models/LocalDate';
import type { OffsetDateTime } from '@client/models/OffsetDateTime';
import type { CancellationMethodType } from '@client/models/service';
import type { AmountWithCurrency } from '@client/models/subscription';
import type { UserId } from '@client/models/user-models';
import type { ZonedDateTime } from '@client/models/ZonedDateTime';
import type { CancellationOutcome } from '@client/modules/cancellation/models';
import type { ActionType } from '@client/modules/InsightsPage/models';
import moment from 'moment';

export type SupplierId = string;
export type ServiceId = string;
export type ContractId = string;
export type SubscriptionId = string;
export const BindingTimeMonitoringThresholdInDays = 40;

export enum BindingPeriodType {
  UnknownContractBindingPeriod = 'UnknownContractBindingPeriod',
  NoContractBindingPeriod = 'NoContractBindingPeriod',
  FixedContractBindingPeriod = 'FixedContractBindingPeriod',
}

export type ContractBindingPeriod =
  | {
      type: BindingPeriodType.UnknownContractBindingPeriod | BindingPeriodType.NoContractBindingPeriod;
      endsAt?: undefined;
      setByUserAt?: undefined;
    }
  | FixedContractBindingPeriod;

export interface FixedContractBindingPeriod {
  type: BindingPeriodType.FixedContractBindingPeriod;
  endsAt: LocalDate;
  setByUserAt: LocalDate;
}

export enum ContractState {
  Active = 'Active',
  Terminating = 'Terminating',
  Terminated = 'Terminated',
}

export interface Contract {
  id: ContractId;
  service: SubscriptionService;
  contractBindingPeriod: ContractBindingPeriod;
  state: ContractState;
  terminable: boolean;
  changePlanEnabled: boolean;
  changeDataplanRequestDate?: string;
  cancellationInfo?: CancellationInfo;
  lastCompletedAction?: ActionType;
  terminatedAt?: OffsetDateTime;
  contractInformation?: ContractInformation;
  terminationFormData?: {
    answers: any[];
    desiredTerminationAt?: string;
  };
  cancellationMethod: { type: CancellationMethodType };
}

export interface ContractInformation {
  type: ContractInformationType;
}

enum ContractInformationType {
  MobileContractInformation = 'MobileContractInformation',
  ElectricityContractInformation = 'ElectricityContractInformation',
  BroadbandContractInformation = 'BroadbandContractInformation',
}

export interface BindingTimeContract extends Contract {
  contractBindingPeriod: FixedContractBindingPeriod;
}

export interface SubscriptionSupplier {
  id: SupplierId;
  name: string;
  logoUrl: string;
  singleServiceSupplier?: boolean;
  verified?: boolean;
}
export interface SubscriptionService {
  id: ServiceId;
  name: string;
  category: ServiceCategory;
  terminationInstructions: any;
  terminationQuestions: Question[];
}

export interface ServiceCategory {
  name: CategoryName;
  text: string;
}

export interface CancellationInfo {
  cancellationId: string;
  cancellationRequestDate: string;
  cancelledAt?: string;
  cancellationReminderSentDate?: string;
  userRating?: number;
  messageToUser?: string;
  actionOutcome?: CancellationOutcome;
  yearlySavings?: AmountWithCurrency;
  bindingPeriodEndsAt?: string;
  lastPaymentAt?: string;
}

export enum SubscriptionSource {
  BankDiscovery = 'bank-discovery',
  Manual = 'manual',
  Converted = 'bank-discovery-override',
  Api = 'api',
}

export interface Subscription {
  id: SubscriptionId;
  createdAt: ZonedDateTime;
  supplier: SubscriptionSupplier;
  cost: AmountWithCurrency;
  paymentInterval: PaymentInterval;
  source: SubscriptionSource;
  bankEvents?: BankEvent[];
  nextPaymentDate?: string;
  state: SubscriptionState;
  singleContractId?: ContractId;
  contracts: Contract[];
  providerId?: string;
  userMessage: UserMessage;
  paid?: boolean;
}

export enum FrontendSubscriptionType {
  LockedSubscription = 'LockedSubscription',
  TerminatedSubscription = 'TerminatedSubscription',
  OtherSubscription = 'OtherSubscription',
}

export enum SubscriptionState {
  MoreInformationNeeded = 'MoreInformationNeeded',
  Active = 'Active',
  OneContractTerminating = 'OneContractTerminating',
  MultipleContractsTerminating = 'MultipleContractsTerminating',
  MultipleTasksOngoing = 'MultipleTasksOngoing',
  Terminated = 'Terminated',
}

export interface UserMessage {
  id: string;
  userId: UserId;
  contractId: ContractId;
  createdAt: string;
  message: { type: string };
}

export interface PaymentInterval {
  amount: number;
  unit: IntervalUnit;
}

export type IntervalUnit = 'day' | 'week' | 'month' | 'year';

export interface BankEvent {
  date: string;
  description: string;
  cost: AmountWithCurrency;
}

export function isWithinBindingPeriod(contract: Contract): contract is BindingTimeContract {
  if (
    contract.contractBindingPeriod.type === BindingPeriodType.FixedContractBindingPeriod &&
    moment(contract.contractBindingPeriod.endsAt).isAfter(moment())
  ) {
    return true;
  } else {
    return false;
  }
}

export const hasTerminationInstructions = (contract?: Contract): boolean =>
  contract?.service.terminationInstructions ?? false;
